/* ==========================================================================
   PAMPER & PIECE — scent-visualizer.js
   Renders a scent's note-intensity profile as a lightweight inline-SVG radar
   (the "scent fingerprint"). NO external charting library — the whole mark is
   plain SVG, themed entirely through CSS custom properties, reduced-motion
   aware, and paired with a visually-hidden data table so the numbers are always
   reachable by screen readers, print, and forced-colors modes.

   Data comes from the product's pamper.note_intensity metafield (JSON), passed
   via data-notes on a [data-scent-radar] element. Accepts any of:
     [4,3,5,2,4,3]                                  -> default axes
     [{"axis":"Sweet","value":4}, ...]              -> labelled
     {"Sweet":4,"Woody":3,"Fresh":5}                -> object map
   Used on the PDP and on the quiz result card (called again after reveal).

   Public API (unchanged): Pamper.renderRadar(el) / Pamper.renderRadars(root).
   ========================================================================== */
(function () {
  'use strict';
  const Pamper = (window.Pamper = window.Pamper || {});
  const DEFAULT_AXES = ['Top', 'Heart', 'Base', 'Sillage', 'Longevity', 'Warmth'];
  const STYLE_ID = 'pp-radar-styles';

  /* ---- data normalisation (unchanged contract) ---- */
  function normalize(raw) {
    let labels = [];
    let values = [];
    if (!raw) return null;
    let data = raw;
    if (typeof raw === 'string') {
      try {
        data = JSON.parse(raw);
      } catch (e) {
        return null;
      }
    }
    if (Array.isArray(data)) {
      if (data.length && typeof data[0] === 'object') {
        labels = data.map((d) => d.axis || d.label || d.name || '');
        values = data.map((d) => Number(d.value) || 0);
      } else {
        values = data.map((d) => Number(d) || 0);
        labels = DEFAULT_AXES.slice(0, values.length);
      }
    } else if (typeof data === 'object') {
      labels = Object.keys(data);
      values = labels.map((k) => Number(data[k]) || 0);
    }
    if (!values.length) return null;
    return { labels, values };
  }

  /* ---- geometry helpers ---- */
  // Angle 0 points straight up; axes fan clockwise from there.
  function polar(cx, cy, r, angleDeg) {
    const a = ((angleDeg - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  }
  const round = (n) => Math.round(n * 100) / 100;
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function getCss(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  /* ---- one shared stylesheet, injected on first render ---- */
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = [
      '.pp-radar{display:block;width:100%;margin:0;position:relative}',
      '.pp-radar__svg{display:block;width:100%;height:auto;overflow:visible}',
      // Recessive grid: single-hue ink, well below the data.
      '.pp-radar__ring,.pp-radar__spoke{fill:none;stroke:var(--pp-radar-ink,currentColor);stroke-width:1}',
      '.pp-radar__ring{stroke-opacity:.10}',
      '.pp-radar__spoke{stroke-opacity:.14}',
      // Data area: one hue, translucent fill + 2px stroke. transform-origin centre for the draw-on.
      '.pp-radar__area{fill:color-mix(in srgb,var(--pp-radar-primary,#e0457b) 18%,transparent);' +
        'stroke:var(--pp-radar-primary,#e0457b);stroke-width:2;stroke-linejoin:round;' +
        'transform-origin:50% 50%}',
      // Vertices: ≥7px, secondary fill, 2px surface ring so overlaps stay legible.
      '.pp-radar__dot{fill:var(--pp-radar-secondary,#c9a227);stroke:var(--pp-radar-surface,#fff);' +
        'stroke-width:2;transition:r .15s ease}',
      '.pp-radar__dot:hover{r:4.6}',
      // Axis labels wear the ink token, never the series colour.
      '.pp-radar__label{fill:var(--pp-radar-ink,currentColor);' +
        'font-family:var(--pp-font-body,system-ui,sans-serif);font-size:9px;font-weight:600;letter-spacing:.01em}',
      // Accessible data table — visually hidden, always in the DOM.
      '.pp-radar__table{position:absolute;width:1px;height:1px;padding:0;margin:-1px;' +
        'overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}',
      '@media (prefers-reduced-motion:no-preference){' +
        '.pp-radar__area{animation:pp-radar-in .7s cubic-bezier(.16,1,.3,1) both}' +
        '.pp-radar__dot{animation:pp-radar-dot .5s ease .25s both}' +
        '@keyframes pp-radar-in{from{transform:scale(.15);opacity:0}to{transform:scale(1);opacity:1}}' +
        '@keyframes pp-radar-dot{from{opacity:0}to{opacity:1}}}',
    ].join('');
    document.head.appendChild(s);
  }

  /* ---- render one radar into its [data-scent-radar] mount ---- */
  function render(el) {
    if (!el || el.dataset.rendered === 'true') return;
    const parsed = normalize(el.dataset.notes);
    if (!parsed) {
      el.style.display = 'none';
      return;
    }
    el.dataset.rendered = 'true';
    injectStyles();

    const primary = el.dataset.primary || getCss('--pp-primary') || '#e0457b';
    const secondary = el.dataset.secondary || getCss('--pp-secondary') || '#c9a227';
    const ink = getCss('--pp-text') || '#1a1413';
    const surface = el.dataset.bg || getCss('--pp-bg') || '#fff';
    el.style.setProperty('--pp-radar-primary', primary);
    el.style.setProperty('--pp-radar-secondary', secondary);
    el.style.setProperty('--pp-radar-ink', ink);
    el.style.setProperty('--pp-radar-surface', surface);

    const N = parsed.values.length;
    const maxVal = Math.max(5, ...parsed.values);
    const rings = maxVal <= 6 ? Math.round(maxVal) : 5;
    const SIZE = 200;
    const C = SIZE / 2;
    const R = 72; // leave room for outer labels

    // Concentric grid rings (recessive).
    let grid = '';
    for (let lvl = 1; lvl <= rings; lvl++) {
      const rr = (R * lvl) / rings;
      const pts = [];
      for (let i = 0; i < N; i++) {
        const [x, y] = polar(C, C, rr, (i * 360) / N);
        pts.push(round(x) + ',' + round(y));
      }
      grid += '<polygon class="pp-radar__ring" points="' + pts.join(' ') + '"/>';
    }

    // Spokes + outer axis labels.
    let spokes = '';
    let labels = '';
    for (let i = 0; i < N; i++) {
      const ang = (i * 360) / N;
      const [x, y] = polar(C, C, R, ang);
      spokes += '<line class="pp-radar__spoke" x1="' + C + '" y1="' + C + '" x2="' + round(x) + '" y2="' + round(y) + '"/>';
      const [lx, ly] = polar(C, C, R + 13, ang);
      const cos = Math.cos(((ang - 90) * Math.PI) / 180);
      const anchor = Math.abs(cos) < 0.3 ? 'middle' : cos < 0 ? 'end' : 'start';
      labels +=
        '<text class="pp-radar__label" x="' + round(lx) + '" y="' + round(ly) + '" text-anchor="' + anchor +
        '" dominant-baseline="middle">' + esc(parsed.labels[i] || '') + '</text>';
    }

    // Data polygon + vertices (with native title tooltips = the hover layer).
    const dpts = [];
    let dots = '';
    for (let i = 0; i < N; i++) {
      const v = Math.max(0, Math.min(parsed.values[i], maxVal));
      const [x, y] = polar(C, C, (R * v) / maxVal, (i * 360) / N);
      dpts.push(round(x) + ',' + round(y));
      dots +=
        '<circle class="pp-radar__dot" cx="' + round(x) + '" cy="' + round(y) + '" r="3.4"><title>' +
        esc(parsed.labels[i] || '') + ': ' + v + '</title></circle>';
    }

    const svg =
      '<svg class="pp-radar__svg" viewBox="0 0 ' + SIZE + ' ' + SIZE + '" aria-hidden="true" focusable="false">' +
      '<g class="pp-radar__grid">' + grid + spokes + '</g>' +
      '<polygon class="pp-radar__area" points="' + dpts.join(' ') + '"/>' +
      '<g class="pp-radar__dots">' + dots + '</g>' +
      '<g class="pp-radar__labels">' + labels + '</g>' +
      '</svg>';

    // Visually-hidden data table — the required "table view" fallback.
    const caption = el.getAttribute('aria-label') || 'Scent intensity profile';
    let rows = '';
    for (let i = 0; i < N; i++) {
      rows += '<tr><th scope="row">' + esc(parsed.labels[i] || '') + '</th><td>' + parsed.values[i] + '</td></tr>';
    }
    const table =
      '<table class="pp-radar__table"><caption>' + esc(caption) + '</caption>' +
      '<thead><tr><th scope="col">Note</th><th scope="col">Intensity</th></tr></thead>' +
      '<tbody>' + rows + '</tbody></table>';

    // The figure carries the accessible name via its own caption/table now, so
    // drop the img role/label to avoid masking the table from assistive tech.
    el.removeAttribute('role');
    el.removeAttribute('aria-label');
    el.classList.add('pp-radar');
    el.innerHTML = svg + table;
  }

  function renderAll(root) {
    (root || document).querySelectorAll('[data-scent-radar]').forEach(render);
  }

  Pamper.renderRadar = render;
  Pamper.renderRadars = renderAll;

  // No external dependency to wait for — render as soon as the DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      renderAll();
    });
  } else {
    renderAll();
  }
})();
